import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  EconomicMetrics,
  AdminEconomicSettings,
  UpdateAdminEconomicSettingsRequest,
} from 'types/interfaces/economic';

/**
 * Economic metrics API service
 * Provides endpoints for fetching platform-wide economic health metrics
 * and admin configuration for economic settings
 */
export const economicApi = createApi({
  reducerPath: 'economicApi',
  baseQuery: baseQuery,
  tagTypes: ['EconomicMetrics', 'EconomicSettings'],
  endpoints: (builder) => ({
    /**
     * Get all economic health metrics for the platform
     * Public endpoint - no authentication required
     * Backend caches results for 5 minutes
     */
    getEconomicMetrics: builder.query<EconomicMetrics, void>({
      query: () => ({
        url: '/economic/metrics',
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: ['EconomicMetrics'],
      // Keep data for 5 minutes (300 seconds) to complement backend caching
      keepUnusedDataFor: 300,
    }),

    /**
     * Get admin economic settings (admin only)
     * Includes target points per active trader
     */
    getAdminEconomicSettings: builder.query<AdminEconomicSettings, void>({
      query: () => ({
        url: '/admin/settings/target-points',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['EconomicSettings'],
    }),

    /**
     * Update admin economic settings (admin only)
     * Updates target points per active trader
     */
    updateAdminEconomicSettings: builder.mutation<
      AdminEconomicSettings,
      UpdateAdminEconomicSettingsRequest
    >({
      query: (body) => ({
        url: '/admin/settings/target-points',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      // Invalidate both tags to refresh dashboard after admin changes
      invalidatesTags: ['EconomicSettings', 'EconomicMetrics'],
    }),
  }),
});

export const {
  useGetEconomicMetricsQuery,
  useGetAdminEconomicSettingsQuery,
  useUpdateAdminEconomicSettingsMutation,
} = economicApi;
